import {
  NOTIFICATION_LIMITS,
  type NotificationSettings,
  type NotificationView,
} from '@mogzo/shared';
import { useEffect, useState } from 'react';
import { apiFetch } from './api';

export type { NotificationSettings, NotificationView };
export type NotificationFilter = 'all' | 'games' | 'communities';

export interface NotificationPage {
  notifications: NotificationView[];
  unread: number;
  hasMore: boolean;
}

export function fetchNotifications(filter: NotificationFilter, before?: string) {
  const query = new URLSearchParams({ filter });
  if (before) query.set('before', before);
  return apiFetch<NotificationPage>(`/v1/me/notifications?${query}`);
}

/** Marks these read, or all of them; answers with what's still unread. */
export function markRead(ids?: string[]) {
  return apiFetch<{ unread: number }>('/v1/me/notifications/read', {
    method: 'POST',
    body: JSON.stringify(ids ? { ids } : {}),
  });
}

export function fetchNotificationSettings() {
  return apiFetch<NotificationSettings>('/v1/me/notification-settings');
}

export function saveNotificationSettings(change: Partial<Omit<NotificationSettings, 'muted'>>) {
  return apiFetch<NotificationSettings>('/v1/me/notification-settings', {
    method: 'PATCH',
    body: JSON.stringify(change),
  });
}

/** "Email me again" for a game. */
export function unmuteGame(projectId: string) {
  return apiFetch<NotificationSettings>(`/v1/me/notification-settings/muted/${projectId}`, {
    method: 'DELETE',
  });
}

/** What an email's stop link stops: one game (its name), or all game emails (null). */
export function fetchStopLink(token: string) {
  return apiFetch<{ game: string | null }>(`/v1/notifications/stop?t=${encodeURIComponent(token)}`);
}

export function stopEmails(token: string) {
  return apiFetch<{ game: string | null }>(
    `/v1/notifications/stop?t=${encodeURIComponent(token)}`,
    { method: 'POST' },
  );
}

const listeners = new Set<(unread: number | null) => void>();
let latest: number | null = null;
let stopPolling: (() => void) | null = null;

/** Tells every bell on the page the new count (after marking read). */
export function shareUnread(unread: number) {
  latest = unread;
  for (const listener of listeners) listener(unread);
}

export function refreshUnread() {
  apiFetch<{ unread: number }>('/v1/me/notifications/unread')
    .then((result) => shareUnread(result.unread))
    .catch(() => undefined);
}

/** One poll for the whole page, however many bells it shows. */
function startPolling() {
  refreshUnread();
  const timer = window.setInterval(() => {
    if (document.visibilityState === 'visible') refreshUnread();
  }, NOTIFICATION_LIMITS.pollMs);
  const onVisible = () => document.visibilityState === 'visible' && refreshUnread();
  document.addEventListener('visibilitychange', onVisible);
  return () => {
    window.clearInterval(timer);
    document.removeEventListener('visibilitychange', onVisible);
  };
}

/**
 * The bell's count, asked for every minute while the page is open (which is also how MOGZO
 * knows the player is here, so their turn needs no email). Null until it's known.
 */
export function useUnread(signedIn: boolean) {
  const [unread, setUnread] = useState<number | null>(latest);
  useEffect(() => {
    if (!signedIn) return;
    listeners.add(setUnread);
    stopPolling ??= startPolling();
    return () => {
      listeners.delete(setUnread);
      if (!listeners.size) {
        stopPolling?.();
        stopPolling = null;
      }
    };
  }, [signedIn]);
  return signedIn ? unread : null;
}

/** "5 min", "2 h", "Yesterday", "3 days", then the date. */
export function shortAgo(iso: string, now = Date.now()): string {
  const minutes = Math.floor((now - new Date(iso).getTime()) / 60_000);
  if (minutes < 1) return 'Now';
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} h`;
  const days = Math.floor(hours / 24);
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days} days`;
  return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}
